import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, ArrowRight } from "lucide-react";

/**
 * Password reset landing page.
 *
 * Supabase sends the user here from the recovery email. `detectSessionInUrl`
 * on the supabase client parses the recovery token and puts the user into a
 * short-lived recovery session, so `updateUser({ password })` succeeds.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    if (password !== confirm) {
      setError("As duas senhas precisam ser iguais.");
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError("A senha precisa ter no mínimo 6 caracteres.");
      setLoading(false);
      return;
    }
    try {
      await updatePassword(password);
      setInfo("Senha atualizada com sucesso. Redirecionando...");
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err) {
      setError(err?.message || "Não foi possível atualizar a senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen bg-[#0B0C10] flex items-center justify-center px-4"
    >
      <div className="w-full max-w-md bg-[#14161C] border border-[#282D3D] rounded-2xl p-6 shadow-2xl">
        <h1 className="text-lg font-extrabold text-white mb-1">
          Redefinir <span className="text-[#FF6B00]">senha</span>
        </h1>
        <p className="text-xs text-gray-400 mb-5">
          Escolha uma nova senha para acessar sua Caverna.
        </p>

        {error && (
          <div
            data-testid="reset-error-alert"
            className="mb-4 p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-xs text-red-300"
          >
            {error}
          </div>
        )}
        {info && (
          <div
            data-testid="reset-info-alert"
            className="mb-4 p-3 bg-green-500/10 border border-green-500/40 rounded-lg text-xs text-green-300"
          >
            {info}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Nova senha</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="password"
                data-testid="reset-new-password-input"
                required
                minLength={6}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Confirmar senha</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="password"
                data-testid="reset-confirm-password-input"
                required
                minLength={6}
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="reset-submit-btn"
            className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(255,107,0,0.4)] flex items-center justify-center gap-2 mt-2 disabled:opacity-60"
          >
            <span>{loading ? "Salvando..." : "Salvar Nova Senha"}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
